/**
 * TranscriptionProgress
 *
 * Full-screen overlay showing transcription progress with:
 * - Animated progress bar
 * - Stage-by-stage status updates
 * - Estimated time remaining
 * - Tips while waiting
 * - WebSocket real-time updates (with polling fallback)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    View,
    Text,
    Modal,
    ActivityIndicator,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useTranslation } from '../../hooks/useTranslation';
import { TranscriptionStatusResponse } from '../../types';
import { useTaskProgress, useWebSocketConnection } from '../../hooks/useTaskProgress';
import { taskAPI } from '../../services/api';
import { userFacingErrorMessage } from '../../services/errorMessages';
import { jobErrorCode, jobStaleNotice, localizedJobError, type JobStaleNotice } from './jobProgress';
import { brand } from '../../design-system/theme/tokens';

// Design tokens
const COLORS = {
    primary: brand.primary,
    success: '#10B981',
    background: '#FFFFFF',
    surface: '#F9FAFB',
    text: {
        primary: '#111827',
        secondary: '#4B5563',
        muted: '#9CA3AF',
    },
    border: '#E5E7EB',
};

interface TranscriptionProgressProps {
    visible: boolean;
    status: TranscriptionStatusResponse | null;
    estimatedMinutes?: number;
    estimatedSeconds?: number | null;
    estimatedMinSeconds?: number | null;
    estimatedMaxSeconds?: number | null;
    /** Celery task ID for WebSocket progress updates */
    taskId?: string | null;
    /** Called after the user cancels the running task */
    onCancel?: (taskId?: string | null) => void;
}

type EffectiveTranscriptionState = {
    status?: string;
    progress?: number;
    stage?: string;
    error?: string | null;
};

const ETA_TICK_MS = 1000;

const TRANSCRIPTION_PHASES = [
    {
        id: 'validating',
        key: 'transcription.progress.validating',
        fallback: 'Validating audio file...',
        stageIds: ['queued', 'pending', 'validating'],
        minProgress: 5,
        remainingWeight: 0.95,
    },
    {
        id: 'uploading',
        key: 'transcription.progress.uploading',
        fallback: 'Preparing audio...',
        stageIds: ['uploading'],
        minProgress: 15,
        remainingWeight: 0.85,
    },
    {
        id: 'transcribing',
        key: 'transcription.progress.transcribing',
        fallback: 'Transcribing with AI...',
        stageIds: ['transcribing'],
        minProgress: 30,
        remainingWeight: 0.65,
    },
    {
        id: 'identifying_speakers',
        key: 'transcription.progress.identifyingSpeakers',
        fallback: 'Identifying speakers...',
        stageIds: ['identifying_speakers', 'diarizing', 'analyzing_audio_cues', 'detecting_silences'],
        minProgress: 45,
        remainingWeight: 0.45,
    },
    {
        id: 'correcting_grammar',
        key: 'transcription.progress.correctingGrammar',
        fallback: 'Applying grammar correction...',
        stageIds: ['correcting_grammar'],
        minProgress: 58,
        remainingWeight: 0.3,
    },
    {
        id: 'encrypting',
        key: 'transcription.progress.encrypting',
        fallback: 'Encrypting and storing...',
        stageIds: ['encrypting', 'storing', 'complete'],
        minProgress: 70,
        remainingWeight: 0.15,
    },
];

const STAGE_ORDER = TRANSCRIPTION_PHASES.reduce<Record<string, number>>((acc, phase, index) => {
    phase.stageIds.forEach((stageId) => {
        acc[stageId] = index;
    });
    return acc;
}, {});

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function phaseForStage(stage: string | undefined) {
    const index = STAGE_ORDER[stage || ''] ?? 0;
    return TRANSCRIPTION_PHASES[clamp(index, 0, TRANSCRIPTION_PHASES.length - 1)];
}

function normalizeTranscriptionProgress(stage: string | undefined, rawProgress?: number): number {
    if (stage === 'complete') return 100;
    const phase = phaseForStage(stage);
    const value = Number.isFinite(rawProgress) ? Number(rawProgress) : 0;
    return clamp(Math.max(value, phase.minProgress), phase.minProgress, 99);
}

// Map transcription status to progress percentage
function getProgressFromStatus(status: string | undefined, apiProgress?: number, stage?: string): number {
    if (apiProgress && apiProgress > 0) return normalizeTranscriptionProgress(stage, apiProgress);

    switch (status) {
        case 'pending':
            return 5;
        case 'processing':
            return normalizeTranscriptionProgress(stage || 'transcribing', apiProgress);
        case 'completed':
            return 100;
        case 'failed':
            return 100;
        default:
            return 0;
    }
}

// Get stage message based on status
function getStageMessage(status: string | undefined, t: (key: string) => string): string {
    switch (status) {
        case 'pending':
            return t('transcription.progress.validating');
        case 'processing':
            return t('transcription.progress.transcribing');
        case 'completed':
            return t('transcription.progress.complete');
        case 'failed':
            return t('transcription.errors.transcriptionFailed');
        default:
            return t('transcription.progress.processing');
    }
}

// Get stages with completion status
function getStages(
    currentStatus: string | undefined,
    t: (key: string, defaultText?: string) => string,
    wsStage?: string
): {
    id: string;
    label: string;
    completed: boolean;
    active: boolean;
}[] {
    const stages = TRANSCRIPTION_PHASES.map((phase) => ({
        id: phase.id,
        label: t(phase.key, phase.fallback),
    }));

    // Determine completion based on status
    const isCompleted = currentStatus === 'completed';
    const isFailed = currentStatus === 'failed';

    // If we have WebSocket stage info, use it for precise tracking
    const currentStageIndex = wsStage ? (STAGE_ORDER[wsStage] ?? -1) : -1;

    return stages.map((stage, index) => {
        let completed = false;
        let active = false;

        if (isCompleted) {
            completed = true;
        } else if (isFailed) {
            // Show stages up to failure point as completed
            completed = currentStageIndex >= 0 ? index < currentStageIndex : false;
            active = currentStageIndex >= 0 ? index === currentStageIndex : false;
        } else if (currentStageIndex >= 0) {
            // WebSocket provides precise stage - use it
            completed = index < currentStageIndex;
            active = index === currentStageIndex;
        } else if (currentStatus === 'processing') {
            // Fallback: During processing without WebSocket, show first 2 as complete
            completed = index < 2;
            active = index === 2;
        } else if (currentStatus === 'pending') {
            // Pending state - first stage is active
            active = index === 0;
        }

        return { ...stage, completed, active };
    });
}

function hasRealProgressSignal(progress: { stage?: string; message?: string; progress?: number } | null): boolean {
    if (!progress) return false;
    return Boolean(progress.stage || progress.message || (progress.progress ?? 0) > 0);
}

export function TranscriptionProgress({
    visible,
    status,
    estimatedMinutes = 2,
    estimatedSeconds,
    estimatedMinSeconds,
    estimatedMaxSeconds,
    taskId,
    onCancel,
}: TranscriptionProgressProps) {
    const { t } = useTranslation();
    const { useFallback: wsUnavailable } = useWebSocketConnection();
    const [startedAt, setStartedAt] = useState<number | null>(null);
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [polledState, setPolledState] = useState<EffectiveTranscriptionState | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);
    // Stuck-job presentation (never a client-side failure): the server's job
    // row + reaper decide when a transcription failed.
    const [staleNotice, setStaleNotice] = useState<JobStaleNotice>(null);
    const waitingDismissedRef = useRef(false);
    const lostTrackResetAtRef = useRef<number | null>(null);
    const pollNowRef = useRef<(() => void) | null>(null);

    // Use WebSocket progress when available, fall back to polling status
    const wsProgress = useTaskProgress(taskId, {
        onComplete: (result) => {
            console.log('[TranscriptionProgress] Task completed via WebSocket:', result);
        },
        onError: (error) => {
            console.warn('[TranscriptionProgress] Task error via WebSocket:', error);
        },
    });

    useEffect(() => {
        if (!visible) {
            setStartedAt(null);
            setPolledState(null);
            setIsCancelling(false);
            setCancelError(null);
            return;
        }
        setIsCancelling(false);
        setCancelError(null);
        setStartedAt((value) => value ?? Date.now());
    }, [visible, taskId]);

    useEffect(() => {
        if (!visible) return;
        const intervalId = setInterval(() => setNowMs(Date.now()), ETA_TICK_MS);
        return () => clearInterval(intervalId);
    }, [visible]);

    useEffect(() => {
        if (!visible || !taskId) return;

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const pollStartedAt = Date.now();
        let lastStatusAt = pollStartedAt;
        let bestProgress = 0;
        waitingDismissedRef.current = false;
        lostTrackResetAtRef.current = null;
        setStaleNotice(null);

        const updateStaleNotice = () => {
            setStaleNotice(jobStaleNotice({
                now: Date.now(),
                startedAt: pollStartedAt,
                lastStatusAt,
                bestProgress,
                waitingDismissed: waitingDismissedRef.current,
                lostTrackResetAt: lostTrackResetAtRef.current,
            }));
        };

        const poll = async () => {
            try {
                const taskStatus = await taskAPI.getStatus(taskId);
                if (cancelled) return;
                lastStatusAt = Date.now();

                const isCancelled = taskStatus.status === 'REVOKED';
                const stage = taskStatus.stage || (
                    taskStatus.status === 'SUCCESS'
                        ? 'complete'
                        : taskStatus.status === 'FAILURE' || isCancelled
                            ? 'failed'
                            : taskStatus.status === 'PROCESSING'
                                ? 'transcribing'
                                : 'validating'
                );
                const mappedStatus =
                    taskStatus.status === 'SUCCESS'
                        ? 'completed'
                        : taskStatus.status === 'FAILURE' || isCancelled
                            ? 'failed'
                            : taskStatus.status === 'PENDING'
                                ? 'pending'
                                : 'processing';

                const nextProgress = normalizeTranscriptionProgress(stage, taskStatus.progress);
                bestProgress = Math.max(bestProgress, nextProgress);
                const isTerminal = ['SUCCESS', 'FAILURE', 'REVOKED'].includes(taskStatus.status);
                setPolledState({
                    status: mappedStatus,
                    progress: nextProgress,
                    stage,
                    error: taskStatus.status === 'FAILURE' || isCancelled
                        ? localizedJobError(
                            t,
                            taskStatus as any,
                            isCancelled ? 'Task cancelled' : t('transcription.progress.failed'),
                        )
                        : null,
                });

                if (!isTerminal) {
                    updateStaleNotice();
                    timeoutId = setTimeout(poll, 5000);
                } else {
                    setStaleNotice(null);
                }
            } catch {
                if (!cancelled) {
                    updateStaleNotice();
                    timeoutId = setTimeout(poll, 8000);
                }
            }
        };

        pollNowRef.current = () => {
            if (cancelled) return;
            if (timeoutId) clearTimeout(timeoutId);
            void poll();
        };

        timeoutId = setTimeout(poll, 6000);

        return () => {
            cancelled = true;
            pollNowRef.current = null;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [visible, taskId, t]);

    const handleKeepWaiting = () => {
        waitingDismissedRef.current = true;
        setStaleNotice(null);
    };

    const handleCheckAgain = () => {
        lostTrackResetAtRef.current = Date.now();
        setStaleNotice(null);
        pollNowRef.current?.();
    };

    // Merge WebSocket progress with polling status
    // WebSocket takes priority when available and has data
    const effectiveStatus = useMemo(() => {
        // If WebSocket has real progress data, use it.
        if (wsProgress && !wsUnavailable && hasRealProgressSignal(wsProgress)) {
            // Map WebSocket stage to status
            let mappedStatus: string;
            if (wsProgress.isComplete && !wsProgress.error) {
                mappedStatus = 'completed';
            } else if (wsProgress.error) {
                mappedStatus = 'failed';
            } else if (wsProgress.stage === 'validating' || wsProgress.stage === 'uploading') {
                mappedStatus = 'pending';
            } else {
                mappedStatus = 'processing';
            }

            return {
                status: mappedStatus,
                progress: normalizeTranscriptionProgress(wsProgress.stage, wsProgress.progress),
                stage: wsProgress.stage || 'validating',
                // Translated from the stable job error code when present;
                // otherwise map the English text as before.
                error: wsProgress.error
                    ? (jobErrorCode({ error_code: wsProgress.errorCode, error_message_key: wsProgress.errorMessageKey })
                        ? localizedJobError(
                            t,
                            { error_code: wsProgress.errorCode, error_message_key: wsProgress.errorMessageKey, error: wsProgress.error },
                            wsProgress.error,
                        )
                        : userFacingErrorMessage(t, wsProgress.error))
                    : null,
            };
        }
        if (polledState) {
            // Polled errors are already localized from the job's error code.
            return polledState;
        }

        // Fall back to document polling status supplied by parent.
        const fallbackStatus = status?.status ?? (visible ? 'pending' : undefined);
        const fallbackStage =
            fallbackStatus === 'completed'
                ? 'complete'
                : fallbackStatus === 'pending'
                    ? 'validating'
                    : fallbackStatus === 'processing'
                        ? 'transcribing'
                        : undefined;
        return {
            status: fallbackStatus,
            progress: getProgressFromStatus(fallbackStatus, status?.progress, fallbackStage),
            stage: fallbackStage,
            error: status?.error ? userFacingErrorMessage(t, status.error) : null,
        };
    }, [wsProgress, wsUnavailable, polledState, status, visible, t]);

    const progress = getProgressFromStatus(effectiveStatus.status, effectiveStatus.progress, effectiveStatus.stage);
    const stages = getStages(effectiveStatus.status, t, effectiveStatus.stage);
    const isCompleted = effectiveStatus.status === 'completed';
    const isFailed = effectiveStatus.status === 'failed';
    const currentPhase = phaseForStage(effectiveStatus.stage);
    const stageMessage = isCompleted || isFailed
        ? getStageMessage(effectiveStatus.status, t)
        : t(currentPhase.key, currentPhase.fallback);

    const handleCancel = async () => {
        if (isCancelling || isCompleted || isFailed) return;
        setCancelError(null);
        setIsCancelling(true);
        try {
            if (taskId) {
                await taskAPI.cancel(taskId);
            }
            onCancel?.(taskId);
        } catch (error) {
            console.warn('[TranscriptionProgress] Cancel failed:', error);
            setCancelError(t('transcription.progress.cancelFailed'));
            setIsCancelling(false);
        }
    };
    const estimateFromWs = wsProgress?.metadata?.estimated_completion_seconds;
    const estimateMinFromWs = wsProgress?.metadata?.estimated_completion_min_seconds;
    const estimateMaxFromWs = wsProgress?.metadata?.estimated_completion_max_seconds;
    const totalEstimateMs = Math.max(
        60_000,
        Number(estimatedSeconds || estimateFromWs || estimatedMinutes * 60) * 1000
    );
    const totalEstimateMinMs = estimatedMinSeconds || estimateMinFromWs
        ? Math.max(30_000, Number(estimatedMinSeconds || estimateMinFromWs) * 1000)
        : Math.max(30_000, totalEstimateMs * 0.65);
    const totalEstimateMaxMs = estimatedMaxSeconds || estimateMaxFromWs
        ? Math.max(60_000, Number(estimatedMaxSeconds || estimateMaxFromWs) * 1000)
        : Math.max(60_000, totalEstimateMs * 1.6);

    const etaText = useMemo(() => {
        if (!visible || isCompleted || isFailed || !startedAt) return null;

        const elapsedMs = Math.max(0, nowMs - startedAt);
        const totalSeconds = Math.max(1, Math.round(elapsedMs / 1000));
        const formatDuration = (durationMs: number): string => {
            const seconds = Math.max(1, Math.round(durationMs / 1000));
            if (seconds < 60) {
                return t('analysis.progress.secondsShort', { seconds });
            }
            const minutes = Math.max(1, Math.round(seconds / 60));
            return t('analysis.progress.minutesShort', { minutes });
        };

        const elapsedText = totalSeconds < 60
            ? t('analysis.progress.secondsShort', { seconds: totalSeconds })
            : t('analysis.progress.minutesShort', { minutes: Math.max(1, Math.round(totalSeconds / 60)) });

        const progressRemainingMs = progress > 5
            ? Math.max(20_000, (elapsedMs / progress) * (100 - progress))
            : totalEstimateMs;
        const phaseRemainingMs = Math.max(
            20_000,
            totalEstimateMs * currentPhase.remainingWeight
        );
        const remainingMs = Math.max(20_000, (progressRemainingMs * 0.6) + (phaseRemainingMs * 0.4));
        const rangeLowerMs = Math.max(20_000, Math.min(remainingMs * 0.75, totalEstimateMinMs * currentPhase.remainingWeight));
        const rangeUpperMs = Math.max(rangeLowerMs, Math.max(remainingMs * 1.35, totalEstimateMaxMs * currentPhase.remainingWeight));
        const remainingText = rangeUpperMs < 60_000
            ? t('analysis.progress.etaUnderMinute')
            : Math.abs(rangeUpperMs - rangeLowerMs) < 60_000
                ? t('analysis.progress.etaAboutDuration', { duration: formatDuration(remainingMs) })
                : t('analysis.progress.etaRangeDuration', {
                    min: formatDuration(rangeLowerMs),
                    max: formatDuration(rangeUpperMs),
                });

        return t(
            'analysis.progress.etaWithElapsed',
            { elapsed: elapsedText, remaining: remainingText }
        );
    }, [
        visible,
        isCompleted,
        isFailed,
        startedAt,
        nowMs,
        progress,
        totalEstimateMs,
        totalEstimateMinMs,
        totalEstimateMaxMs,
        currentPhase.remainingWeight,
        t,
    ]);

    const checkpointText = useMemo(() => {
        if (isCompleted || isFailed) return null;
        const index = STAGE_ORDER[effectiveStatus.stage || ''] ?? 0;
        const label = t(currentPhase.key, currentPhase.fallback);
        return t('analysis.progress.checkpoint', {
            current: clamp(index, 0, TRANSCRIPTION_PHASES.length - 1) + 1,
            total: TRANSCRIPTION_PHASES.length,
            label,
        });
    }, [currentPhase, effectiveStatus.stage, isCompleted, isFailed, t]);

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            statusBarTranslucent={true}
            onRequestClose={!isCompleted && !isFailed ? handleCancel : undefined}
        >
            <View style={styles.overlay}>
                <View style={styles.container}>
                    {/* Header */}
                    <View style={styles.header}>
                        <MaterialIcons
                            name={isCompleted ? 'check-circle' : isFailed ? 'error' : 'headphones'}
                            size={48}
                            color={isCompleted ? COLORS.success : isFailed ? '#EF4444' : COLORS.primary}
                        />
                        <Text style={styles.title}>{t('transcription.progress.title')}</Text>
                    </View>

                    {/* Progress Bar */}
                    <View style={styles.progressContainer}>
                        <View style={styles.progressBackground}>
                            <View
                                style={[
                                    styles.progressFill,
                                    { width: `${progress}%` },
                                    isCompleted && { backgroundColor: COLORS.success },
                                    isFailed && { backgroundColor: '#EF4444' },
                                ]}
                            />
                        </View>
                        <Text style={styles.progressText}>{Math.round(progress)}%</Text>
                    </View>

                    {/* Current Stage */}
                    <View style={styles.stageRow}>
                        {!isCompleted && !isFailed && (
                            <ActivityIndicator size="small" color={COLORS.primary} style={{ marginRight: 8 }} />
                        )}
                        <Text style={styles.stageMessage}>{stageMessage}</Text>
                    </View>

                    {/* Stages List */}
                    <View style={styles.stagesList}>
                        {stages.map((stage) => (
                            <View key={stage.id} style={styles.stageItem}>
                                <MaterialIcons
                                    name={stage.completed ? 'check-circle' : stage.active ? 'radio-button-checked' : 'radio-button-unchecked'}
                                    size={18}
                                    color={stage.completed ? COLORS.success : stage.active ? COLORS.primary : COLORS.text.muted}
                                />
                                <Text style={[
                                    styles.stageLabel,
                                    stage.completed && styles.stageLabelCompleted,
                                    stage.active && styles.stageLabelActive,
                                ]}>
                                    {stage.label}
                                </Text>
                            </View>
                        ))}
                    </View>

                    {/* Time Estimate */}
                    {!isCompleted && !isFailed && (
                        <View style={styles.timeContainer}>
                            {etaText && (
                                <View style={styles.timeRow}>
                                    <MaterialIcons name="schedule" size={16} color={COLORS.primary} />
                                    <Text style={styles.estimatedTime}>{etaText}</Text>
                                </View>
                            )}
                            {checkpointText && (
                                <Text style={styles.checkpointText}>{checkpointText}</Text>
                            )}
                            {cancelError && (
                                <Text style={styles.cancelErrorText}>{cancelError}</Text>
                            )}
                        </View>
                    )}

                    {/* Stuck-job notice: the job keeps running; the user decides */}
                    {!isCompleted && !isFailed && staleNotice && (
                        <View style={styles.staleNotice} testID="transcription-stale-notice">
                            <Text style={styles.staleNoticeText}>
                                {staleNotice === 'waitingInLine'
                                    ? t('jobs.progress.waitingInLineTranscription')
                                    : t('jobs.progress.lostTrack')}
                            </Text>
                            <TouchableOpacity
                                style={styles.staleNoticeButton}
                                onPress={staleNotice === 'waitingInLine' ? handleKeepWaiting : handleCheckAgain}
                                testID={staleNotice === 'waitingInLine' ? 'transcription-keep-waiting' : 'transcription-check-again'}
                            >
                                <Text style={styles.staleNoticeButtonText}>
                                    {staleNotice === 'waitingInLine'
                                        ? t('jobs.progress.keepWaiting')
                                        : t('jobs.progress.checkAgain')}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* Tip */}
                    {!isCompleted && !isFailed && (
                        <View style={styles.tipContainer}>
                            <Text style={styles.tipIcon}>💡</Text>
                            <Text style={styles.tipText}>{t('transcription.progress.tip')}</Text>
                        </View>
                    )}

                    {/* Error Message */}
                    {isFailed && effectiveStatus.error && (
                        <View style={styles.errorContainer}>
                            <Text style={styles.errorText}>{effectiveStatus.error}</Text>
                        </View>
                    )}

                    {!isCompleted && !isFailed && (
                        <TouchableOpacity
                            style={[styles.cancelButton, isCancelling && styles.cancelButtonDisabled]}
                            onPress={handleCancel}
                            disabled={isCancelling}
                        >
                            <MaterialIcons name="close" size={18} color="#DC2626" />
                            <Text style={styles.cancelButtonText}>
                                {isCancelling
                                    ? t('transcription.progress.cancelling')
                                    : t('transcription.progress.cancel')}
                            </Text>
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    container: {
        backgroundColor: COLORS.background,
        borderRadius: 16,
        padding: 24,
        width: '100%',
        maxWidth: 400,
        alignItems: 'center',
    },
    header: {
        alignItems: 'center',
        marginBottom: 24,
    },
    title: {
        fontSize: 22,
        fontWeight: '700',
        color: COLORS.text.primary,
        marginTop: 12,
    },
    progressContainer: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    progressBackground: {
        flex: 1,
        height: 12,
        backgroundColor: COLORS.surface,
        borderRadius: 6,
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        backgroundColor: COLORS.primary,
        borderRadius: 6,
    },
    progressText: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.text.secondary,
        marginLeft: 12,
        minWidth: 40,
        textAlign: 'right',
    },
    stageRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 20,
    },
    stageMessage: {
        fontSize: 16,
        fontWeight: '600',
        color: COLORS.primary,
    },
    stagesList: {
        width: '100%',
        paddingHorizontal: 8,
        marginBottom: 20,
    },
    stageItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 6,
    },
    stageLabel: {
        fontSize: 14,
        color: COLORS.text.muted,
        marginLeft: 10,
    },
    stageLabelCompleted: {
        color: COLORS.success,
    },
    stageLabelActive: {
        color: COLORS.text.primary,
        fontWeight: '600',
    },
    estimatedTime: {
        fontSize: 13,
        color: COLORS.primary,
        fontWeight: '600',
        flex: 1,
        textAlign: 'center',
    },
    timeContainer: {
        width: '100%',
        backgroundColor: 'rgba(30, 64, 175, 0.08)',
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 12,
        marginBottom: 16,
    },
    timeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
    },
    checkpointText: {
        marginTop: 4,
        fontSize: 12,
        color: COLORS.text.secondary,
        textAlign: 'center',
    },
    cancelErrorText: {
        marginTop: 6,
        fontSize: 12,
        color: '#DC2626',
        textAlign: 'center',
    },
    staleNotice: {
        backgroundColor: COLORS.surface,
        borderColor: COLORS.border,
        borderWidth: 1,
        borderRadius: 10,
        padding: 12,
        marginBottom: 16,
        alignItems: 'center',
        gap: 10,
    },
    staleNoticeText: {
        fontSize: 14,
        color: COLORS.text.primary,
        textAlign: 'center',
    },
    staleNoticeButton: {
        borderColor: COLORS.primary,
        borderWidth: 1,
        borderRadius: 8,
        paddingVertical: 8,
        paddingHorizontal: 16,
    },
    staleNoticeButtonText: {
        color: COLORS.primary,
        fontSize: 14,
        fontWeight: '600',
    },
    tipContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(30, 64, 175, 0.08)',
        borderRadius: 8,
        padding: 12,
        marginTop: 8,
    },
    tipIcon: {
        fontSize: 16,
        marginRight: 8,
    },
    tipText: {
        fontSize: 13,
        color: COLORS.primary,
        flex: 1,
    },
    errorContainer: {
        backgroundColor: '#FEF2F2',
        borderRadius: 8,
        padding: 12,
        marginTop: 8,
        width: '100%',
    },
    errorText: {
        fontSize: 14,
        color: '#DC2626',
        textAlign: 'center',
    },
    cancelButton: {
        marginTop: 14,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        minHeight: 40,
        paddingHorizontal: 14,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(220, 38, 38, 0.35)',
        backgroundColor: '#FFFFFF',
    },
    cancelButtonDisabled: {
        opacity: 0.6,
    },
    cancelButtonText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#DC2626',
    },
});

export default TranscriptionProgress;
